// DEPENDENCIES
import { FormikValues } from "formik";
import { doc } from "@firebase/firestore";

// TYPES
import { PitbullCountry, Step } from "../../types";

// UTILS
import {
  dniRequired,
  radioOrSingleSelectRequired,
  tramitNumberRequired,
} from "../../validations";
import { onContinueRenaper } from "../../app/utils";
import { genderOptions } from "../../utils";
import { sendIndividualDocument } from "../../connectors/connectors";
import { documentTypesParsed, firestoreCollections } from "../../constants";

// COMPONENTS
import LivenessValidationCard from "../../components/LivenessValidationCard/LivenessValidationCard";
import UploadDniCard from "../../components/UploadDniCard/UploadDniCard";

import EditIcon from "../../assets/images/icons/ui/edit.svg?react";

const IdentityStep = (countries: PitbullCountry[]): Omit<Step, "step"> => {
  return {
    name: "Identidad",
    disableButtonBack: true,
    subSteps: [
      {
        step: 1,
        fields: [
          {
            placeholder: "XX.XXX.XXX",
            initialValue: "",
            type: "dni",
            name: "identityDocument",
            label: "DNI",
            validation: dniRequired,
            disabled: true,
            tooltip: {
              label: <EditIcon />,
              modal: "modal-edit-dni",
              external: true,
            },
          },
          {
            placeholder: "XXXXXXXXXXX",
            initialValue: "",
            type: "text",
            name: "tramitNumber",
            label: "Número de trámite",
            tooltip: {
              label: "¿Dónde lo encuentro?",
              modal: "modal-tramit-number",
            },
            validation: tramitNumberRequired,
            infoText: "Ingresá los 11 dígitos, incluyendo ceros iniciales.",
          },
          {
            name: "gender",
            type: "multiple-radio",
            placeholder: "Seleccioná una opción",
            validation: radioOrSingleSelectRequired(genderOptions),
            initialValue: "",
            options: genderOptions,
            label: "Género",
          },
        ],
        onContinue: async (values: FormikValues, dispatch, state) => {
          if (!dispatch || !values || !state) return;
          return await onContinueRenaper(values, dispatch, state, countries);
        },
      },
      {
        step: 2,
        label: "uploadDNI", // Used to identify the step in remoteSteps
        subtitle: "Cargá fotos del frente y dorso de tu DNI.",
        component: (dispatch, state, formik) => (
          <UploadDniCard
            dispatch={dispatch}
            state={state}
            formik={formik}
            getDniRef={({ db, uuid }) =>
              doc(db, firestoreCollections.dni, uuid)
            }
            onUploadDni={async (type, file, uuid, cuit) => {
              await sendIndividualDocument({
                draftUUID: uuid,
                documentType: documentTypesParsed[type],
                file,
                cuit: cuit,
              });
            }}
          />
        ),
        insideForm: true,
      },
      {
        step: 3, // Used to identify the step in remoteSteps
        component: (dispatch, state, formik) => (
          <LivenessValidationCard
            dispatch={dispatch}
            state={state}
            formik={formik}
            getLivenessRef={({ db, uuid }) =>
              doc(db, firestoreCollections.liveness, uuid)
            }
            onUploadLiveness={async (file, uuid, cuit) => {
              await sendIndividualDocument({
                draftUUID: uuid,
                documentType: "SELFIE",
                file,
                cuit,
              });
            }}
          />
        ),
        insideForm: true,
      },
    ],
    disableBackNavigation: true,
  };
};

export default IdentityStep;
